<template>
  <div class="webviewer-wrapper fill-height">
    <div v-if="docLoaded && meta.pageCount"
         class="d-flex justify-space-between">
      <v-progress-linear height="32"
                         color="light-blue"
                         :value="progressValue">
        <template v-slot:default="{value}">
          Page {{ meta.currentPage }} / {{ meta.pageCount }}
        </template>
      </v-progress-linear>
    </div>
    <div id="cad_viewer"
         class="fill-height">
    </div>
  </div>
</template>
<script>
import {mapGetters} from 'vuex';
import WebViewer from '@pdftron/webviewer'
import {find, merge} from 'lodash';
import pdftron from "../../../../../pdftron/pdftron";
import bugsnagService from '../../../services/bugsnagService';

export default {
  name: 'view-cad-presentation',
  props: {
    presentation: {
      type: Object,
      required: true,
    },
    session: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      busy: false,
      instance: null,
      docLoaded: false,
    }
  },
  computed: {
    ...mapGetters([
      'authenticated',
      'isAdmin',
      'isEmployee',
      'isMember',
      'isSuperAdmin',
      'team',
      'user',
    ]),
    ...mapGetters('content', [
      'activity',
      'activityID',
    ]),
    ...mapGetters('meta', [
      'meta',
    ]),
    annotationManager() {
      return this.instance ? this.instance.Core.annotationManager : null;
    },
    documentViewer() {
      return this.instance ? this.instance.Core.documentViewer : null;
    },
    file() {
      if (!this.presentation) {
        return null;
      }
      return find(this.presentation.files, file => ['presentation.dwg', 'presentation.dxf'].includes(file.path));
    },
    canBeDownloaded() {
      return this.presentation && !!this.presentation.settings.can_be_exported;
    },
    canBePrinted() {
      return this.presentation && !!this.presentation.settings.can_be_printed_by_user;
    },
    progressValue() {
      if (this.docLoaded
          && this.meta.currentPage
          && this.meta.pageCount) {
        return parseInt((this.meta.currentPage / this.meta.pageCount) * 100);
      }
      return null;
    },
  },
  async beforeDestroy() {
    try {
      if (this.instance) {
        // await this.instance.UI.closeDocument()
        // this.instance.UI.dispose();
        this.instance = null;
      }
    } catch (e) {
      console.log('silently caught exception in beforeDestroy()', e.message);
    }
  },
  created() {
    this.busy = true;
  },
  watch: {
    presentation(val, oldVal) {
      if (val && oldVal && val !== oldVal) {
        console.log(`Changed from presentation ${oldVal.id} to ${val.id}`);
        if (this.instance && this.file) {
          if (this.documentViewer) {
            this.documentViewer.dispose();
          }
          this.instance.loadDocument(this.file.url);
        }
      }
    }
  },
  mounted() {
    this.loadViewer();
  },
  methods: {
    goToPrevPage() {
      let currentPage = this.documentViewer.getCurrentPage();
      if (currentPage > 1) {
        // @see https://www.pdftron.com/api/web/CoreControls.DocumentViewer.html#setCurrentPage__anchor
        this.setCurrentPage(currentPage - 1);
      }
    },
    setCurrentPage(page) {
      this.documentViewer.setCurrentPage(page, true);
    },
    goToNextPage() {
      let currentPage = this.documentViewer.getCurrentPage();
      let pageCount = this.documentViewer.getPageCount();
      if (currentPage < pageCount) {
        // @see https://www.pdftron.com/api/web/CoreControls.DocumentViewer.html#setCurrentPage__anchor
        this.setCurrentPage(currentPage + 1);
      }
    },
    createViewer(url, viewerElement, options = {}) {
      // @see https://www.pdftron.com/api/web/global.html#WebViewerOptions__anchor
      const config = {
        css: '/webviewer.css',
        // https://www.pdftron.com/documentation/web/guides/hiding-elements#list-of-elements
        disableLogs: false, // Disables console logs coming from WebViewer, including the version and build numbers
        //forceClientSideInit: true, // If set to true then when loading a document using WebViewer Server the document will always switch to client only rendering allowing page manipulation and the full API to be used.
        fullAPI: true, // Enable PDFNet.js library functions
        initialDoc: url, // URL path to a document to load on startup
        licenseKey: pdftron.LICENSE_KEY, // License key for viewing documents. If not set then WebViewer will be in demo mode.
        path: '/v2/js/WebViewer', // Path to the WebViewer lib folder
        webviewerServerURL: pdftron.getWebViewerServerURL(), // MLD-2790 The URL path to the hosted WebViewer Server
        //preloadWorker: 'pdf', // Type of workers to be preloaded. Accepts `pdf`|`office`|`legacyOffice`|`all`. You can also include multiple types comma separated e.g. `pdf,office`
        ui: 'legacy', // MLD-6881
      };
      merge(config, options);
      //console.log('config', config);
      return WebViewer.Iframe(config, viewerElement); // @see https://www.pdftron.com/documentation/web/guides/integrate
    },
    async loadViewer() {
      if (!this.presentation) {
        return;
      }
      if (this.presentation.type_cad) {
        const url = this.file.url;
        const options = {
          disabledElements: [],
        }
        // @see https://www.pdftron.com/documentation/web/guides/hiding-elements/
        //options.disabledElements.push('leftPanelButton');
        // MLD-2880 I need to enable viewControlsButton and Overlay so we set the LayoutMode
        //options.disabledElements.push('viewControlsButton');
        //options.disabledElements.push('viewControlsOverlay');
        options.disabledElements.push('panToolButton');
        options.disabledElements.push('selectToolButton');
        options.disabledElements.push('freeHandToolGroupButton');
        options.disabledElements.push('freeHandToolButton');
        options.disabledElements.push('searchButton');
        options.disabledElements.push('toggleNotesButton');
        // @see https://www.pdftron.com/documentation/web/guides/customizing-header/#customizing-ribbons
        options.disabledElements.push('ribbons');
        options.disabledElements.push('toolbarGroup-Shapes');
        options.disabledElements.push('toolbarGroup-Edit');
        options.disabledElements.push('toolbarGroup-Insert');
        options.disabledElements.push('toolbarGroup-Annotate');
        options.disabledElements.push('toolsHeader');
        if (!this.canBeDownloaded) {
          options.disabledElements.push('downloadButton');
        }
        if (!this.canBePrinted) {
          options.disabledElements.push('printButton');
        }
        options.disabledElements.push('themeChangeButton');
        options.disabledElements.push('contextMenuPopup');
        const instance = this.instance = await this.createViewer(url, document.getElementById('cad_viewer'), options);

        // @see https://docs.apryse.com/api/web/Core.DocumentViewer.html#.Events__anchor
        const DocumentViewerEvents = instance.Core.DocumentViewer.Events;

        // @see https://docs.apryse.com/api/web/UI.html#.Events__anchor
        const UIEvents = instance.UI.Events;

        // @see https://docs.apryse.com/api/web/UI.html#.Events__anchor
        instance.UI.addEventListener(UIEvents.LOAD_ERROR, (err) => {
          bugsnagService.notify(err); // MLD-5144

          // @see https://docs.apryse.com/api/web/UI.html#.closeElements
          instance.UI.closeElements(['errorModal', 'loadingModal', 'progressModal']);

          // @see https://docs.apryse.com/api/web/UI.html#.showWarningMessage__anchor
          const message = `There was a problem loading the PDF. Click the browser reload button, or press RELOAD. Press the X to cancel.`;
          instance.UI.showWarningMessage({
            title: `PDF Loading Error - Presentation ${this.presentation?.id}`,
            message: message,
            confirmBtnText: 'RELOAD',
            onConfirm: () => {
              console.log('The user clicked the confirm button');
              window.location.reload();
              return Promise.resolve();
            },
            onCancel: () => {
              console.log('The user clicked the cancel button');
              this.$router.push({name: `${this.$root.routePrefix}.home`});
              return Promise.resolve();
            }
          })
        });

        this.configureHeader();
        this.instance.UI.enableElements(['pageNavOverlay']);
        this.busy = false;

        // MLD-2866 try to fix the Scilex blank-page problem
        // @see https://www.pdftron.com/api/web/Core.DocumentViewer.html#event:finishedRendering
        // @see https://support.pdftron.com/support/tickets/26868
        this.documentViewer.addEventListener(DocumentViewerEvents.FINISHED_RENDERING, () => {
          instance.UI.setZoomLevel(instance.UI.getZoomLevel());
        }, {once: true})

        // MLD-2879 Enable using the left/right arrow keys to switch pages.
        // @see https://www.pdftron.com/api/web/Core.DocumentViewer.html#enableArrowKeyNavigation
        this.documentViewer.enableArrowKeyNavigation();

        this.documentViewer.addEventListener(DocumentViewerEvents.DOCUMENT_LOADED, () => {
          // MLD-2880
          // @see https://www.pdftron.com/api/web/UI.html#.LayoutMode__anchor
          instance.UI.setLayoutMode(instance.UI.LayoutMode.Single);
          // @see https://www.pdftron.com/api/web/UI.html#.setFitMode__anchor
          instance.UI.setFitMode(instance.UI.FitMode.FitPage);
          //console.log('documentLoaded() LayoutMode, FitMode');
        });

        // MLD-2880
        this.instance.UI.addEventListener(UIEvents.FULLSCREEN_MODE_TOGGLED, () => {
          instance.UI.setFitMode(instance.UI.FitMode.FitPage);
        })

        this.documentViewer.addEventListener(DocumentViewerEvents.DOCUMENT_LOADED, async () => {
          // call methods relating to the loaded document
          const meta = {
            presentationID: this.presentation.id,
            currentPage: this.documentViewer.getCurrentPage(),
            pageCount: this.documentViewer.getPageCount(),
          }
          await this.$store.dispatch('meta/loadDocument', meta);
          this.docLoaded = true;
          this.pageChanged(this.meta.currentPage); // MLD-4832
          //console.log('documentLoaded() Meta', this.meta);
        })

        // https://www.pdftron.com/api/web/CoreControls.DocumentViewer.html#event:documentUnloaded__anchor
        this.documentViewer.addEventListener(DocumentViewerEvents.DOCUMENT_UNLOADED, async () => {
          this.$store.commit('meta/unloadDocument');
          this.docLoaded = false;
          //console.log('documentUnloaded');
        })

        // https://www.pdftron.com/api/web/CoreControls.DocumentViewer.html#event:pageNumberUpdated__anchor
        this.documentViewer.addEventListener(DocumentViewerEvents.PAGE_NUMBER_UPDATED, (pageNumber) => {
          //console.log('pageNumberUpdated', pageNumber);
          this.pageChanged(pageNumber)
        });
      } else {
        // TODO display an error message
      }
    },
    pageChanged(pageNumber) { // MLD-4832
      //console.log('changePage', pageNumber);
      this.$emit('change_page', pageNumber);
    },
    configureHeader() {
      const spacer = {
        type: 'spacer',
      };
      const divider = {
        type: 'divider',
      }

      this.instance.UI.setHeaderItems(header => {
        const dataElements = [
          'leftPanelButton',
          'viewControlsButton', // MLD-2880
          'zoomOverlayButton',
          //'menuButton',
        ];
        //console.log('all items', header.getItems());
        const items = header.getItems().filter(item => dataElements.includes(item.dataElement));

        items.push(divider);

        const fullscreenButton = {
          type: 'statefulButton',
          dataElement: 'fullscreenButton2',
          title: 'Toggle full screen mode',
          initialState: 'normal',
          states: {
            normal: {
              number: 1,
              img: 'icon-header-full-screen',
              onClick: (update, activeState) => {
                const isFullScreen = this.instance.UI.isFullscreen();
                this.instance.UI.toggleFullScreen();
                if (isFullScreen) {
                  activeState.number = 1;
                  activeState.img = 'icon-header-full-screen';
                  //console.log('exit full screen');
                } else {
                  activeState.number = 2;
                  activeState.img = 'icon-header-full-screen-exit';
                  //console.log('go full screen');
                }
                update();
              },
            },
          }
        }
        items.push(fullscreenButton);

        if (this.canBePrinted) {
          const printButton = {
            dataElement: 'printButton',
            type: 'actionButton',
            img: 'icon-header-print-line',
            title: 'Print',
            onClick: () => {
              this.instance.UI.print()
            }
          }
        }

        items.push(divider);

        items.push(spacer);

        items.push(divider);
        const previousButton = {
          dataElement: 'previousPageButton',
          type: 'actionButton',
          img: '/images/icons/icons8-left-filled.svg',
          title: 'Previous page',
          onClick: () => {
            this.goToPrevPage();
          }
        };
        items.push(previousButton);
        const nextButton = {
          dataElement: 'nextPageButton',
          type: 'actionButton',
          img: '/images/icons/icons8-right-filled.svg',
          title: 'Next page',
          onClick: () => {
            this.goToNextPage();
          }
        }
        items.push(nextButton);

        header.update(items);
      });
    }
  }
}
</script>
